import { createSelector, createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'

import type { RootState } from '../store'

export const ACTION_STATUS = {
  DEFAULT: 'default',
  SUCCESS: 'success',
  FAILED: 'failed',
} as const

export type ActionStatusType = (typeof ACTION_STATUS)[keyof typeof ACTION_STATUS]

interface CustomerSliceState {
  isLoading: boolean
  customerFetchStatus: ActionStatusType
  data: {
    customerAddress: Record<string, unknown>
    billingAddress: Record<string, unknown>
    deliveryAddresses: Record<string, unknown>[]
    customerNumber: string
  }
}

const initialState: CustomerSliceState = {
  isLoading: false,
  customerFetchStatus: ACTION_STATUS.DEFAULT,
  data: {
    customerAddress: {},
    billingAddress: {},
    deliveryAddresses: [],
    customerNumber: '',
  },
}

const customerSlice = createSlice({
  name: 'customer',
  initialState,
  reducers: {
    updateCustomerSAP(state, action: PayloadAction<string>) {
      state.data.customerAddress.partnerNumber = action.payload
    },
    getCustomerInfo(state) {
      state.isLoading = true
    },
    getCustomerInfoSuccess(state, { payload }) {
      state.isLoading = false
      state.data = payload
      state.customerFetchStatus = ACTION_STATUS.SUCCESS
    },
    getCustomerInfoError(state) {
      state.isLoading = false
      state.customerFetchStatus = ACTION_STATUS.FAILED
    },
  },
})

export const customerActions = customerSlice.actions

export const selectIsFetchingCustomerAddress = (state: RootState) =>
  state.user.isFetchingUserDetails || state.customer.isLoading

export const selectCustomerAddress = (state: RootState) =>
  state.user.userDetails?.customerAddress ?? state.customer.data.customerAddress

export const selectDeliveryAddresses = (state: RootState) =>
  state.user.userDetails?.deliveryAddresses ?? state.customer.data.deliveryAddresses

export const selectCustomerAddressPartnerNumber = (state) =>
  state.user.userDetails?.customerAddress?.partnerNumber
    ?? state.customer.data?.customerAddress?.partnerNumber
    ?? state.user.userDetails?.sapCustomerNumber

export const selectBillingAddress = (state: RootState) =>
  state.user.userDetails?.billingAddress ?? state.customer.data?.billingAddress

export const selectCustomerFetchStatus = (state: RootState) => state?.customer?.customerFetchStatus

export const selectCustomerPhone = (state: RootState) =>
  state.user?.userDetails?.customerAddress?.phoneNumber
    ?? state.customer.data.customerAddress?.phoneNumber

export const selectCustomerNumber = (state: RootState) =>
  state.user.userDetails?.customerNumber ?? state.customer.data.customerNumber

export const selectCustomerName = createSelector(
  (state: RootState) =>
    state.user.userDetails?.customerAddress?.name ?? state.customer.data.customerAddress?.name,
  (state: RootState) =>
    state.user.userDetails?.customerAddress?.name2 ?? state.customer.data?.customerAddress?.name2,
  (name1: string, name2: string) => [name1, name2].filter((name) => name).join(', '),
)

export default customerSlice.reducer
